import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router";
import { useRail } from "../../shared/phone/media";
import { PortalMark } from "../components/Logo";
import { PoweredBy, ProgramTile } from "../components/phone/parts";

const HOW = [
  ["Share your link", "Post your referral link or coupon anywhere you have an audience."],
  ["They become a customer", "Last click wins. Every sale is credited back to you."],
  ["You get paid", "Rewards clear their hold, then the owner pays you directly."],
];

/**
 * Log in and sign up on a phone and an iPad: one full-width form that fits the screen, 16px fields with the
 * right keyboards and AutoFill, the switch to the other as a 44px line under the button. An iPad shows how
 * the program works above a card.
 */
function AuthPage({ children }: { children: ReactNode }) {
  const rail = useRail();
  return (
    <div className={rail ? "po-auth po-auth-ip" : "po-auth"}>
      <div className="po-au">
        <div className="po-au-main">
          <div className="po-au-mark">
            <PortalMark className="plogo po-au-g" />
            <span>Partner portal</span>
          </div>
          {rail ? (
            <section className="po-how" aria-label="How it works">
              <h2>Get paid to share.</h2>
              <ol>
                {HOW.map(([t, s], i) => (
                  <li key={t}>
                    <span className="po-how-n">{i + 1}</span>
                    <b>{t}</b>
                    <span>{s}</span>
                  </li>
                ))}
              </ol>
            </section>
          ) : null}
          <div className="po-au-card">{children}</div>
        </div>
        <PoweredBy />
      </div>
    </div>
  );
}

function Field({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div className="pn-field">
      <label htmlFor={id}>{label}</label>
      {children}
    </div>
  );
}

export function LoginNative({
  email,
  password,
  onEmail,
  onPassword,
  error,
  busy,
  onSubmit,
}: {
  email: string;
  password: string;
  onEmail: (v: string) => void;
  onPassword: (v: string) => void;
  error: string | null;
  busy: boolean;
  onSubmit: (e: FormEvent) => void;
}) {
  return (
    <AuthPage>
      <div className="po-au-h">
        <h1>Log in</h1>
        <p>Pick up where you left off.</p>
      </div>
      <form className="po-au-f" onSubmit={onSubmit}>
        {error ? (
          <p className="po-errbox" role="alert">
            {error}
          </p>
        ) : null}
        <Field id="po-li-email" label="Email">
          <input
            id="po-li-email"
            className="pn-inp"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="off"
            spellCheck={false}
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            required
          />
        </Field>
        <Field id="po-li-pass" label="Password">
          <input id="po-li-pass" className="pn-inp" type="password" autoComplete="current-password" value={password} onChange={(e) => onPassword(e.target.value)} required />
        </Field>
        <button className="pn-btn pri wide" type="submit" disabled={busy}>
          {busy ? "Logging in…" : "Log in"}
        </button>
      </form>
      <Link className="po-au-alt" to="/signup">
        New here? <b>Create an account</b>
      </Link>
    </AuthPage>
  );
}

export function SignupNative({
  productName,
  firstName,
  lastName,
  email,
  phone,
  password,
  onFirst,
  onLast,
  onEmail,
  onPhone,
  onPassword,
  error,
  busy,
  onSubmit,
}: {
  productName: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  onFirst: (v: string) => void;
  onLast: (v: string) => void;
  onEmail: (v: string) => void;
  onPhone: (v: string) => void;
  onPassword: (v: string) => void;
  error: string | null;
  busy: boolean;
  onSubmit: (e: FormEvent) => void;
}) {
  return (
    <AuthPage>
      <div className="po-au-h">
        <p className="po-au-inv">
          {productName ? <ProgramTile name={productName} size={22} /> : null}
          <span>{productName ? `You are invited to the ${productName} partner program` : "You are invited to a partner program"}</span>
        </p>
        <h1>Create your account</h1>
      </div>
      <form className="po-au-f" onSubmit={onSubmit}>
        {error ? (
          <p className="po-errbox" role="alert">
            {error}
          </p>
        ) : null}
        <Field id="po-su-first" label="First name">
          <input id="po-su-first" className="pn-inp" autoComplete="given-name" autoCapitalize="words" value={firstName} onChange={(e) => onFirst(e.target.value)} required />
        </Field>
        <Field id="po-su-last" label="Last name">
          <input id="po-su-last" className="pn-inp" autoComplete="family-name" autoCapitalize="words" value={lastName} onChange={(e) => onLast(e.target.value)} required />
        </Field>
        <Field id="po-su-email" label="Email">
          <input
            id="po-su-email"
            className="pn-inp"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="off"
            spellCheck={false}
            value={email}
            onChange={(e) => onEmail(e.target.value)}
            required
          />
        </Field>
        <Field id="po-su-phone" label="Phone">
          <input id="po-su-phone" className="pn-inp" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => onPhone(e.target.value)} />
        </Field>
        <Field id="po-su-pass" label="Password">
          <input
            id="po-su-pass"
            className="pn-inp"
            type="password"
            autoComplete="new-password"
            placeholder="At least 8 characters"
            minLength={8}
            value={password}
            onChange={(e) => onPassword(e.target.value)}
            required
          />
        </Field>
        <button className="pn-btn pri wide" type="submit" disabled={busy}>
          {busy ? "Creating account…" : "Create account"}
        </button>
      </form>
      <Link className="po-au-alt" to="/login">
        Already a partner? <b>Log in</b>
      </Link>
    </AuthPage>
  );
}
